import React, { ReactElement, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { Node } from '../Node';
import { useConnection } from '../Connections/hooks/use-connection';
import { useDrag } from '../Node/hooks/use-drag';
import { usePosition } from '../Node/hooks/use-position';
import { COMMON_DIAGRAM_ICONS } from '../../constants';
import { CommonIconEditModalContainer } from '../CommonIconEditModal/container';
import {
  ENDPOINT_ICON_PICKER_ICONS,
  ENDPOINT_KIT_TAB_ORDER,
} from '../../../../constants/iconPicker';
import { CommonIconEditButtonContainer } from '../CommonIconEditButton/container';
import styles from './EndpointKitNode.module.css';
import { DefaultEndpointKitNodeDisplay as defaultPosition } from './constants';
import { determineIconToRender } from './utils/utils';

export function EndpointKitNode({
  uid,
  display,
  isReadOnly,
  isSnapToGridEnabled,
  isAddingLogicalConnection,
}: BaseNodeProps): ReactElement {
  const nodeRef = useRef<HTMLDivElement>(null);
  const { redrawNodeConnections } = useConnection();
  const { t } = useTranslation();

  const hookProps = {
    defaultPosition,
    display,
    nodeRef,
    redrawNodeConnections,
    uid,
  };

  usePosition({ ...hookProps });
  useDrag({ ...hookProps, isReadOnly, isSnapToGridEnabled });

  const SVG_CLASSES = classNames({
    [styles.svgCorrection]: display?.icon?.substring(0, 4) === 'Icon',
    [styles.svgOpacity]: display?.hidden,
  });

  const TITLE_CLASSES = classNames(styles.title, {
    [styles.hiddenOpacity]: display?.hidden,
  });

  const ACTIONS_BUTTON_WRAPPER_CLASSES = classNames(
    styles.actionButtonsWrapper,
    {
      [styles.hiddenOpacity]: display?.hidden,
    },
  );

  return (
    <>
      <Node
        isCommonNode={true}
        isHidden={display?.hidden}
        className={styles.root}
        ref={nodeRef}
        isReadOnly={isReadOnly}
        uid={uid}
        iconName={display?.icon}
        isConnectionAllowed={false}
        isAddingLogicalConnection={isAddingLogicalConnection}
      >
        <p className={TITLE_CLASSES}>{t('diagram.endpointKit')}</p>
        <div className={ACTIONS_BUTTON_WRAPPER_CLASSES}>
          <CommonIconEditButtonContainer
            isAddingLogicalConnection={isAddingLogicalConnection || false}
            isReadOnly={isReadOnly}
            name={COMMON_DIAGRAM_ICONS.ENDPOINTKIT}
          />
        </div>
        <div className={SVG_CLASSES}>
          {determineIconToRender({
            display,
            uid,
          })}
        </div>
      </Node>
      <CommonIconEditModalContainer
        nodeUid={uid}
        display={display}
        availableIcons={ENDPOINT_ICON_PICKER_ICONS}
        tabOrder={ENDPOINT_KIT_TAB_ORDER}
        existingIcon={display?.icon || 'EndpointKit'}
        nodeType={COMMON_DIAGRAM_ICONS.ENDPOINTKIT}
      />
    </>
  );
}
